// A verified partner's pages (MOTHER-87): My Leads (their standing, then Pipeline and
// Closed) and a lead's page (where it stands, what they typed, Olytic's latest update).
// Props only, so tests render each with fake data; PartnersPage.tsx picks which one.
// My Leads' header links to the Refer a Company form (MOTHER-88, ReferPage.tsx) and My
// Profile (MOTHER-182, ProfilePage.tsx).
// No money but the partner's own referral rate: no deal value, amount, payout or total.
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  type ColumnDef,
  DataTable,
  DetailPanel,
  EmptyState,
  ErrorState,
  InlineMessage,
  MetricRow,
  PageContent,
  PageHeader,
  Stack,
  StatusTimeline,
  Text,
} from '@olytic/foundation';
import type { MouseEvent, ReactNode } from 'react';

import type { PartnerMe, Referral } from './api';
import { calendarDate, leadPath, localDate, MY_LEADS_PATH, percent, personName, PROFILE_PATH, REFER_PATH, splitLeads, stageLabel, stageSteps } from './leads';
import { isPlainClick, PageLink } from './PageLink';

const NOT_PROVIDED = 'Not provided';

/** What every verified partner page can ask for. */
export interface LeadPageActions {
  busy: boolean;
  /** A failed Sign Out: the session is still live, and the button retries. */
  error: string | undefined;
  onSignOut(): void;
  /** Moves to My Leads or a lead's page without a page load. */
  onOpen(path: string): void;
}

/**
 * The page frame: a PageHeader with the page's own header action (if any) and Sign Out,
 * a failed Sign Out's message, then the page. `width` is PageContent's: `default` suits a
 * form page.
 */
export function LeadsFrame({
  title,
  breadcrumbs,
  headerAction,
  width = 'wide',
  actions,
  children,
}: {
  title: string;
  breadcrumbs?: ReactNode;
  headerAction?: ReactNode;
  width?: 'default' | 'wide';
  actions: LeadPageActions;
  children: ReactNode;
}): JSX.Element {
  return (
    <PageContent as="main" width={width} className="px-3 pb-8 pt-5 sm:px-6">
      <Stack gap="md">
        <PageHeader
          eyebrow="Olytic Partners"
          title={title}
          breadcrumbs={breadcrumbs}
          actions={
            <>
              {headerAction}
              <Button variant="neutral" onClick={actions.onSignOut} loading={actions.busy}>
                Sign Out
              </Button>
            </>
          }
        />
        {actions.error ? (
          <InlineMessage tone="error" role="alert">
            {actions.error}
          </InlineMessage>
        ) : null}
        {children}
      </Stack>
    </PageContent>
  );
}

export function BackToMyLeads({ onOpen }: { onOpen(path: string): void }): JSX.Element {
  return (
    <PageLink path={MY_LEADS_PATH} go={onOpen}>
      Back to My Leads
    </PageLink>
  );
}

/**
 * A My Leads header button: Refer a Company (MOTHER-88) or My Profile (MOTHER-182). A real
 * link (open in a new tab works) drawn as Foundation's Button, which moves the page itself
 * on a plain click.
 */
function HeaderLink({ path, variant, onOpen, children }: { path: string; variant: 'brand' | 'neutral'; onOpen(path: string): void; children: string }): JSX.Element {
  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!isPlainClick(event)) return;
    event.preventDefault();
    onOpen(path);
  };
  return (
    <Button asChild variant={variant}>
      <a className="oly-reset" href={path} onClick={onClick}>
        {children}
      </a>
    </Button>
  );
}

/** The partner's standing: since when, their referral rate, and their Olytic contact. */
function Standing({ me }: { me: PartnerMe }): JSX.Element {
  const partner = me.partner;
  const contact = partner?.olyticContact ?? null;
  const rate = partner?.referralRate;
  return (
    <MetricRow
      aria-label="Your Standing"
      layout="wrap"
      metrics={[
        { key: 'partnerSince', label: 'Partner Since', value: calendarDate(partner?.partnerSince) || 'Not recorded' },
        { key: 'referralRate', label: 'Referral Rate', value: typeof rate === 'number' ? percent(rate) : 'Not set' },
        contact === null
          ? { key: 'olyticContact', label: 'Your Olytic Contact', value: 'Not assigned yet' }
          : { key: 'olyticContact', label: 'Your Olytic Contact', value: contact.name ?? contact.email, ...(contact.name ? { sub: contact.email } : {}) },
      ]}
    />
  );
}

const COLUMNS: ColumnDef<Referral>[] = [
  { key: 'contact', label: 'Contact', primary: true, get: personName },
  { key: 'company', label: 'Company', get: (row) => row.companyName ?? '' },
  { key: 'stage', label: 'Stage', get: (row) => stageLabel(row.stage), mobileHeader: true },
  { key: 'submitted', label: 'Submitted', get: (row) => localDate(row.createdAt), nowrap: true },
  { key: 'stageChanged', label: 'Stage Changed', get: (row) => localDate(row.stageChangedAt), nowrap: true },
];

/** One section of My Leads: a read-only table whose Contact cells open the lead, or "No leads yet." */
function LeadSection({ id, title, rows, onOpen }: { id: string; title: string; rows: Referral[]; onOpen(path: string): void }): JSX.Element {
  return (
    <Card>
      <CardHeader title={title} titleId={id} count={rows.length} headingLevel={2} />
      <CardBody>
        {rows.length === 0 ? (
          <EmptyState title="No leads yet." />
        ) : (
          <DataTable
            aria-labelledby={id}
            columns={COLUMNS}
            rows={rows}
            getRowId={(row) => row.id}
            object="lead"
            recordHref={(_object, rowId) => leadPath(rowId)}
            onNavigate={onOpen}
            fit
          />
        )}
      </CardBody>
    </Card>
  );
}

/** My Leads (`/`): the partner's standing, then Pipeline (open leads) and Closed (finished ones), newest first. */
export function MyLeadsPage({ me, referrals, ...actions }: { me: PartnerMe; referrals: Referral[] } & LeadPageActions): JSX.Element {
  const { pipeline, closed } = splitLeads(referrals);
  return (
    <LeadsFrame
      title="My Leads"
      headerAction={
        <>
          <HeaderLink path={REFER_PATH} variant="brand" onOpen={actions.onOpen}>
            Refer a Company
          </HeaderLink>
          <HeaderLink path={PROFILE_PATH} variant="neutral" onOpen={actions.onOpen}>
            My Profile
          </HeaderLink>
        </>
      }
      actions={actions}
    >
      <Standing me={me} />
      <LeadSection id="partner-pipeline" title="Pipeline" rows={pipeline} onOpen={actions.onOpen} />
      <LeadSection id="partner-closed" title="Closed" rows={closed} onOpen={actions.onOpen} />
    </LeadsFrame>
  );
}

const text = (value: string | null): string => (value && value.trim() !== '' ? value : NOT_PROVIDED);

/**
 * A lead's page (`/leads/<id>`): where it stands, what the partner typed, and Olytic's
 * latest update. `notice` heads it just after the partner submitted it (MOTHER-88).
 */
export function LeadPage({ referral, notice, ...actions }: { referral: Referral; notice?: string | undefined } & LeadPageActions): JSX.Element {
  const title = referral.companyName ? `${personName(referral)}, ${referral.companyName}` : personName(referral);
  return (
    <LeadsFrame title={title} breadcrumbs={<BackToMyLeads onOpen={actions.onOpen} />} actions={actions}>
      {notice ? (
        <InlineMessage tone="success" role="status">
          {notice}
        </InlineMessage>
      ) : null}
      <Card>
        <CardHeader title={`Stage: ${stageLabel(referral.stage)}`} headingLevel={2} />
        <CardBody>
          <StatusTimeline steps={stageSteps(referral)} />
        </CardBody>
      </Card>
      <Card>
        <CardHeader title="Update From Olytic" headingLevel={2} />
        <CardBody>
          {referral.update ? (
            <Stack gap="sm">
              <Text>{referral.update.text}</Text>
              {referral.update.at ? (
                <Text tone="muted" size="sm">
                  {localDate(referral.update.at)}
                </Text>
              ) : null}
            </Stack>
          ) : (
            <Text tone="muted">No update yet.</Text>
          )}
        </CardBody>
      </Card>
      <Card>
        <CardHeader title="Details" headingLevel={2} />
        <CardBody>
          <DetailPanel
            fields={[
              { key: 'firstName', label: 'First Name', value: text(referral.firstName) },
              { key: 'lastName', label: 'Last Name', value: text(referral.lastName) },
              { key: 'email', label: 'Email', value: text(referral.email) },
              { key: 'phone', label: 'Phone', value: text(referral.phone) },
              { key: 'title', label: 'Title', value: text(referral.title) },
              { key: 'companyName', label: 'Company', value: text(referral.companyName) },
              { key: 'companyWebsite', label: 'Website', value: text(referral.companyWebsite) },
              { key: 'relationship', label: 'How You Know Them', value: text(referral.relationship), wide: true },
              { key: 'need', label: 'What They Are Looking For', value: text(referral.need), wide: true },
              { key: 'timeline', label: 'Timeline', value: text(referral.timeline) },
            ]}
          />
        </CardBody>
      </Card>
    </LeadsFrame>
  );
}

/** A lead id the API answered 404 for: another partner's, or made up. */
export function NoSuchLeadPage(actions: LeadPageActions): JSX.Element {
  return (
    <LeadsFrame title="My Leads" actions={actions}>
      <EmptyState title="No such lead." action={<BackToMyLeads onOpen={actions.onOpen} />} />
    </LeadsFrame>
  );
}

/** The leads could not be read (a 503 or the network): worth another try. */
export function LeadsFailedPage({ onRetry, ...actions }: { onRetry(): void } & LeadPageActions): JSX.Element {
  return (
    <LeadsFrame title="My Leads" actions={actions}>
      <Stack gap="md">
        {/* ErrorState's own button reads Retry; the page offers Try Again, as the criteria ask. */}
        <ErrorState title="Something went wrong" description="We could not load your leads. Please try again." />
        <Button variant="neutral" onClick={onRetry}>
          Try Again
        </Button>
      </Stack>
    </LeadsFrame>
  );
}
